// Utility: Calculate discount percentage from value
export const calculateDiscountPercentage = (
  discountValue: number,
  catalogPrice: number,
): number => {
  if (catalogPrice === 0) return 0;
  return (discountValue / catalogPrice) * 100;
};

// Utility: Calculate discount value from percentage
export const calculateDiscountValue = (
  discountPercentage: number,
  catalogPrice: number,
): number => {
  return catalogPrice * (discountPercentage / 100);
};

// Interfaces and Types
export interface ProjectItemCalculation {
  id?: string;
  amountWithoutVat: string;
  amountWithoutVatBccy?: string;
  totalPurchase: string;
  purchasePriceBccy?: string;
  catalogPriceBccy: string;
  catalogPrice?: string;
  currency: string;
  probability: string;
  finalDate: string | null;
}

export interface ChangeFlags {
  isDealValueChanged: boolean;
  isPurchaseCostChanged: boolean;
  isCatalogPriceChanged: boolean;
  isDiscountChanged: boolean;
  isDiscountValueChanged: boolean;
  isMarginPercentageChanged: boolean;
  isMarginValueChanged: boolean;
}

export interface ProjectCalculationState {
  calculation: ProjectItemCalculation;
  original: ProjectItemCalculation;
  discountPercentage: string;
  discountValueBccy: string; // Discount in base currency (CZK)
  discountValue: string; // Discount in selected currency
  tempCurrency: string;
  selectedCurrency: string;
  currencyRate: string;
  tempRate: string;
  changeFlags: ChangeFlags;
}

export type ProjectCalculationAction =
  | { type: "LOAD_PROJECT_ITEM"; payload: any }
  | {
      type: "UPDATE_CALCULATION";
      field: keyof ProjectItemCalculation;
      value: any;
    }
  | { type: "UPDATE_DEAL_VALUE"; value: string }
  | { type: "CANCEL_DEAL_VALUE" }
  | { type: "SAVE_DEAL_VALUE" }
  | { type: "UPDATE_PURCHASE_COST"; value: string }
  | { type: "CANCEL_PURCHASE_COST" }
  | { type: "SAVE_PURCHASE_COST" }
  | { type: "UPDATE_CATALOG_PRICE"; value: string }
  | { type: "CANCEL_CATALOG_PRICE" }
  | { type: "SAVE_CATALOG_PRICE" }
  | { type: "UPDATE_DISCOUNT_PERCENTAGE"; value: string; catalogPrice: number }
  | { type: "UPDATE_DISCOUNT_VALUE"; value: string; catalogPrice: number }
  | {
      type: "UPDATE_DISCOUNT_VALUE_SELECTED_CCY";
      value: string;
      catalogPrice: number;
    }
  | { type: "UPDATE_CURRENCY_RATE"; rate: string }
  | { type: "UPDATE_TEMP_RATE"; rate: string }
  | { type: "UPDATE_TEMP_CURRENCY"; currency: string }
  | { type: "UPDATE_SELECTED_CURRENCY"; currency: string }
  | { type: "APPLY_CURRENCY_RATE" }
  | { type: "CANCEL_CURRENCY_RATE" }
  | { type: "UPDATE_FINAL_DATE"; date: string }
  | {
      type: "UPDATE_MARGIN_PERCENTAGE";
      value: string;
      purchaseCost: number;
      rate: number;
    }
  | {
      type: "UPDATE_MARGIN_VALUE";
      value: string;
      purchaseCost: number;
      rate: number;
    }
  | { type: "CANCEL_MARGIN" }
  | { type: "SAVE_MARGIN" }
  | { type: "RESET_CHANGES" };

export interface SelectionState {
  selectedIds: string[];
}

export type SelectionAction =
  | { type: "SET_SELECTED_IDS"; selectedIds: string[] }
  | { type: "CLEAR_SELECTION" };

// Initial States
export const initialProjectCalculationState: ProjectCalculationState = {
  calculation: {
    amountWithoutVat: "0",
    totalPurchase: "0",
    purchasePriceBccy: "0",
    catalogPriceBccy: "0",
    currency: "CZK",
    probability: "0",
    finalDate: null,
  },
  original: {
    amountWithoutVat: "0",
    totalPurchase: "0",
    purchasePriceBccy: "0",
    catalogPriceBccy: "0",
    currency: "CZK",
    probability: "0",
    finalDate: null,
  },
  discountPercentage: "0",
  discountValueBccy: "0",
  discountValue: "0",
  tempCurrency: "CZK",
  selectedCurrency: "CZK",
  currencyRate: "1",
  tempRate: "1",
  changeFlags: {
    isDealValueChanged: false,
    isPurchaseCostChanged: false,
    isCatalogPriceChanged: false,
    isDiscountChanged: false,
    isDiscountValueChanged: false,
    isMarginPercentageChanged: false,
    isMarginValueChanged: false,
  },
};

export const initialSelectionState: SelectionState = {
  selectedIds: [],
};

// Reducers
export function projectCalculationReducer(
  state: ProjectCalculationState,
  action: ProjectCalculationAction,
): ProjectCalculationState {
  switch (action.type) {
    case "LOAD_PROJECT_ITEM": {
      const itemData = action.payload;
      const catalogPrice = parseFloat(itemData.catalogPriceBccy) || 0;
      const dealValue = parseFloat(itemData.amountWithoutVatBccy) || 0;
      const rate = parseFloat(state.currencyRate) || 1;

      let discountPct = "0";
      let discountValBccy = "0";
      let discountVal = "0";

      if (catalogPrice !== 0) {
        const calcDiscountValBccy = catalogPrice - dealValue;
        const calcDiscountPct = calculateDiscountPercentage(
          calcDiscountValBccy,
          catalogPrice,
        );
        const calcDiscountVal = calcDiscountValBccy / rate;
        discountPct = calcDiscountPct.toFixed(2);
        discountValBccy = calcDiscountValBccy.toFixed(2);
        discountVal = calcDiscountVal.toFixed(2);
      }

      return {
        ...state,
        calculation: itemData,
        original: itemData,
        tempCurrency: itemData.currency,
        selectedCurrency: itemData.currency,
        discountPercentage: discountPct,
        discountValueBccy: discountValBccy,
        discountValue: discountVal,
        changeFlags: initialProjectCalculationState.changeFlags,
      };
    }

    case "UPDATE_CALCULATION":
      return {
        ...state,
        calculation: {
          ...state.calculation,
          [action.field]: action.value,
        },
      };

    case "UPDATE_DEAL_VALUE":
      return {
        ...state,
        calculation: {
          ...state.calculation,
          amountWithoutVat: action.value,
        },
        changeFlags: {
          ...state.changeFlags,
          isDealValueChanged: true,
        },
      };

    case "CANCEL_DEAL_VALUE":
      return {
        ...state,
        calculation: {
          ...state.calculation,
          amountWithoutVat: state.original.amountWithoutVat,
        },
        changeFlags: {
          ...state.changeFlags,
          isDealValueChanged: false,
        },
      };

    case "SAVE_DEAL_VALUE":
      return {
        ...state,
        original: {
          ...state.original,
          amountWithoutVat: state.calculation.amountWithoutVat,
        },
        changeFlags: {
          ...state.changeFlags,
          isDealValueChanged: false,
        },
      };

    case "UPDATE_PURCHASE_COST":
      return {
        ...state,
        calculation: {
          ...state.calculation,
          totalPurchase: action.value,
        },
        changeFlags: {
          ...state.changeFlags,
          isPurchaseCostChanged: true,
        },
      };

    case "CANCEL_PURCHASE_COST":
      return {
        ...state,
        calculation: {
          ...state.calculation,
          totalPurchase: state.original.totalPurchase,
        },
        changeFlags: {
          ...state.changeFlags,
          isPurchaseCostChanged: false,
        },
      };

    case "SAVE_PURCHASE_COST":
      return {
        ...state,
        original: {
          ...state.original,
          totalPurchase: state.calculation.totalPurchase,
        },
        changeFlags: {
          ...state.changeFlags,
          isPurchaseCostChanged: false,
        },
      };

    case "UPDATE_CATALOG_PRICE":
      return {
        ...state,
        calculation: {
          ...state.calculation,
          catalogPriceBccy: action.value,
        },
        changeFlags: {
          ...state.changeFlags,
          isCatalogPriceChanged: true,
        },
      };

    case "CANCEL_CATALOG_PRICE": {
      // Recalculate discount values from original data
      const origCatalogPrice = parseFloat(state.original.catalogPriceBccy) || 0;
      const origDealValue =
        parseFloat(
          state.original.amountWithoutVatBccy ||
            state.original.amountWithoutVat,
        ) || 0;
      const rate = parseFloat(state.currencyRate) || 1;

      let discountPct = "0";
      let discountValBccy = "0";
      let discountVal = "0";

      if (origCatalogPrice !== 0) {
        const calcDiscountValBccy = origCatalogPrice - origDealValue;
        const calcDiscountPct = calculateDiscountPercentage(
          calcDiscountValBccy,
          origCatalogPrice,
        );
        const calcDiscountVal = calcDiscountValBccy / rate;
        discountPct = calcDiscountPct.toFixed(2);
        discountValBccy = calcDiscountValBccy.toFixed(2);
        discountVal = calcDiscountVal.toFixed(2);
      }

      return {
        ...state,
        calculation: {
          ...state.calculation,
          catalogPriceBccy: state.original.catalogPriceBccy,
          amountWithoutVat: state.original.amountWithoutVat,
        },
        discountPercentage: discountPct,
        discountValueBccy: discountValBccy,
        discountValue: discountVal,
        changeFlags: {
          ...state.changeFlags,
          isCatalogPriceChanged: false,
          isDiscountChanged: false,
          isDiscountValueChanged: false,
        },
      };
    }

    case "SAVE_CATALOG_PRICE":
      return {
        ...state,
        original: {
          ...state.original,
          catalogPriceBccy: state.calculation.catalogPriceBccy,
        },
        changeFlags: {
          ...state.changeFlags,
          isCatalogPriceChanged: false,
          isDiscountChanged: false,
          isDiscountValueChanged: false,
        },
      };

    case "UPDATE_DISCOUNT_PERCENTAGE": {
      const rate = parseFloat(state.currencyRate) || 1;
      const discountValBccy = calculateDiscountValue(
        parseFloat(action.value) || 0,
        action.catalogPrice,
      );
      const discountVal = discountValBccy / rate;
      const newDealValue = action.catalogPrice - discountValBccy;
      return {
        ...state,
        discountPercentage: action.value,
        discountValueBccy: discountValBccy.toFixed(2),
        discountValue: discountVal.toFixed(2),
        calculation: {
          ...state.calculation,
          amountWithoutVat: newDealValue.toFixed(2),
        },
        changeFlags: {
          ...state.changeFlags,
          isDiscountChanged: true,
          isCatalogPriceChanged: true,
        },
      };
    }

    case "UPDATE_DISCOUNT_VALUE": {
      const rate = parseFloat(state.currencyRate) || 1;
      const discountValBccy = parseFloat(action.value) || 0;
      const discountPct = calculateDiscountPercentage(
        discountValBccy,
        action.catalogPrice,
      );
      const discountVal = discountValBccy / rate;
      const newDealValue = action.catalogPrice - discountValBccy;
      return {
        ...state,
        discountValueBccy: action.value,
        discountValue: discountVal.toFixed(2),
        discountPercentage: discountPct.toFixed(2),
        calculation: {
          ...state.calculation,
          amountWithoutVat: newDealValue.toFixed(2),
        },
        changeFlags: {
          ...state.changeFlags,
          isDiscountValueChanged: true,
          isCatalogPriceChanged: true,
        },
      };
    }

    case "UPDATE_DISCOUNT_VALUE_SELECTED_CCY": {
      const rate = parseFloat(state.currencyRate) || 1;
      const discountVal = parseFloat(action.value) || 0;
      const discountValBccy = discountVal * rate;
      const discountPct = calculateDiscountPercentage(
        discountValBccy,
        action.catalogPrice,
      );
      // Calculate catalog price in selected currency
      const catalogPriceSelectedCcy = action.catalogPrice / rate;
      // Deal value (selected currency) = Catalog price (selected currency) - Discount value (selected currency)
      const newDealValue = catalogPriceSelectedCcy - discountVal;
      return {
        ...state,
        discountValue: action.value,
        discountValueBccy: discountValBccy.toFixed(2),
        discountPercentage: discountPct.toFixed(2),
        calculation: {
          ...state.calculation,
          amountWithoutVat: newDealValue.toFixed(2),
        },
        changeFlags: {
          ...state.changeFlags,
          isDiscountValueChanged: true,
          isCatalogPriceChanged: true,
        },
      };
    }

    case "UPDATE_CURRENCY_RATE": {
      const newRate = parseFloat(action.rate) || 1;
      const discountValBccy = parseFloat(state.discountValueBccy) || 0;
      // Recalculate discount value in selected currency when rate changes
      const discountVal = discountValBccy / newRate;
      return {
        ...state,
        currencyRate: action.rate,
        discountValue: discountVal.toFixed(2),
      };
    }

    case "UPDATE_TEMP_RATE":
      return {
        ...state,
        tempRate: action.rate,
      };

    case "UPDATE_TEMP_CURRENCY":
      return {
        ...state,
        tempCurrency: action.currency,
      };

    case "UPDATE_SELECTED_CURRENCY":
      return {
        ...state,
        selectedCurrency: action.currency,
      };

    case "APPLY_CURRENCY_RATE":
      return {
        ...state,
        currencyRate: state.tempRate,
        selectedCurrency: state.tempCurrency,
      };

    case "CANCEL_CURRENCY_RATE":
      return {
        ...state,
        tempCurrency: state.selectedCurrency,
        tempRate: state.currencyRate,
      };

    case "UPDATE_FINAL_DATE":
      return {
        ...state,
        calculation: {
          ...state.calculation,
          finalDate: action.date,
        },
      };

    case "UPDATE_MARGIN_PERCENTAGE": {
      // Formula: Deal Value = Purchase Cost + Margin Value
      // Margin Value = Purchase Cost * (Margin % / 100)
      const marginPct = parseFloat(action.value) || 0;
      const marginValueCZK = action.purchaseCost * (marginPct / 100);
      const dealValueCZK = action.purchaseCost + marginValueCZK;
      const dealValue = dealValueCZK / action.rate;
      return {
        ...state,
        calculation: {
          ...state.calculation,
          amountWithoutVat: dealValue.toFixed(2),
        },
        changeFlags: {
          ...state.changeFlags,
          isMarginPercentageChanged: true,
        },
      };
    }

    case "UPDATE_MARGIN_VALUE": {
      // Formula: Deal Value = Purchase Cost + Margin Value
      const marginValueCZK = parseFloat(action.value) || 0;
      const dealValueCZK = action.purchaseCost + marginValueCZK;
      const dealValue = dealValueCZK / action.rate;
      return {
        ...state,
        calculation: {
          ...state.calculation,
          amountWithoutVat: dealValue.toFixed(2),
        },
        changeFlags: {
          ...state.changeFlags,
          isMarginValueChanged: true,
        },
      };
    }

    case "CANCEL_MARGIN":
      return {
        ...state,
        calculation: {
          ...state.calculation,
          amountWithoutVat: state.original.amountWithoutVat,
        },
        changeFlags: {
          ...state.changeFlags,
          isMarginPercentageChanged: false,
          isMarginValueChanged: false,
        },
      };

    case "SAVE_MARGIN":
      return {
        ...state,
        original: {
          ...state.original,
          amountWithoutVat: state.calculation.amountWithoutVat,
        },
        changeFlags: {
          ...state.changeFlags,
          isMarginPercentageChanged: false,
          isMarginValueChanged: false,
        },
      };

    case "RESET_CHANGES":
      return {
        ...state,
        calculation: state.original,
        changeFlags: initialProjectCalculationState.changeFlags,
      };

    default:
      return state;
  }
}

export function selectionReducer(
  state: SelectionState,
  action: SelectionAction,
): SelectionState {
  switch (action.type) {
    case "SET_SELECTED_IDS":
      return {
        ...state,
        selectedIds: action.selectedIds,
      };
    case "CLEAR_SELECTION":
      return {
        ...state,
        selectedIds: [],
      };
    default:
      return state;
  }
}
